import React, { useState, useEffect } from 'react';
import api from '../services/api';

const PageHeader = ({ 
    title, 
    subtitle, 
    customImage = null, 
    pageType = 'default', 
    className = '',
    showContent = true,
    height = 'h-64'
}) => {
    const [headerImage, setHeaderImage] = useState(null);
    const [mobileHeaderImage, setMobileHeaderImage] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchHeaderImage();
    }, [pageType, customImage]);

    const fetchHeaderImage = async () => {
        try {
            setLoading(true);
            
            // If a custom image is provided, use that
            if (customImage) {
                setHeaderImage(customImage);
                setMobileHeaderImage(customImage); // Use same image for mobile if custom
                setLoading(false);
                return;
            }

            // Fetch site settings for header images
            const response = await api.get('/admin/settings/public');
            if (response.data.success) {
                const settings = response.data.data;
                
                // Choose the appropriate header images based on page type
                let imageUrl = '';
                let mobileImageUrl = '';
                
                switch (pageType) {
                    case 'homepage':
                        imageUrl = settings.homepage_header_image || settings.default_header_image;
                        mobileImageUrl = settings.homepage_header_image_mobile || settings.default_header_image_mobile || imageUrl;
                        break;
                    case 'profile':
                        imageUrl = settings.profile_header_image || settings.default_header_image;
                        mobileImageUrl = settings.profile_header_image_mobile || settings.default_header_image_mobile || imageUrl;
                        break;
                    case 'dashboard':
                        imageUrl = settings.dashboard_header_image || settings.default_header_image;
                        mobileImageUrl = settings.dashboard_header_image_mobile || settings.default_header_image_mobile || imageUrl;
                        break;
                    default:
                        imageUrl = settings.default_header_image;
                        mobileImageUrl = settings.default_header_image_mobile || imageUrl;
                        break;
                }
                
                
                setHeaderImage(imageUrl);
                setMobileHeaderImage(mobileImageUrl);
            }
        } catch (error) {
            console.error('Error fetching header image:', error);
            // Use a fallback gradient if no image is available
            setHeaderImage(null);
            setMobileHeaderImage(null);
        } finally {
            setLoading(false);
        }
    };

    // Memoize background styles to prevent unnecessary recalculations
    const [backgroundStyles, setBackgroundStyles] = useState({
        desktop: {},
        mobile: {}
    });

    // Update background styles when images change
    useEffect(() => {
        const getBackgroundStyle = (isMobile = false) => {
            const imageToUse = isMobile ? mobileHeaderImage : headerImage;
            
            if (imageToUse) {
                return {
                    backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.05), rgba(0, 0, 0, 0.05)), url(${imageToUse})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat'
                };
            }
            
            // Fallback gradient based on page type
            const gradients = {
                homepage: 'linear-gradient(to right, rgb(30 58 138), rgb(30 64 175), rgb(67 56 202))',
                profile: 'linear-gradient(to right, rgb(30 58 138), rgb(30 64 175), rgb(29 78 216))',
                dashboard: 'linear-gradient(to right, rgb(17 24 39), rgb(30 58 138), rgb(30 64 175))',
                default: 'linear-gradient(to right, rgb(17 24 39), rgb(30 58 138), rgb(30 64 175))'
            };
            
            return {
                background: gradients[pageType] || gradients.default
            };
        };

        setBackgroundStyles({
            desktop: getBackgroundStyle(false),
            mobile: getBackgroundStyle(true)
        });
    }, [headerImage, mobileHeaderImage, pageType]);

    if (loading) {
        return (
            <div className={`${height} bg-gray-900 animate-pulse flex items-center justify-center ${className}`}>
                <div className="text-center">
                    <div className="w-32 h-8 bg-gray-700 rounded mb-2 mx-auto"></div>
                    <div className="w-48 h-4 bg-gray-700 rounded mx-auto"></div>
                </div>
            </div>
        );
    }

    return (
        <div className={`${height} relative flex items-center justify-center ${className}`}>
            {/* Desktop Background */}
            <div 
                className="absolute inset-0 hidden lg:block"
                style={backgroundStyles.desktop}
            ></div>
            
            {/* Mobile Background */}
            <div 
                className="absolute inset-0 lg:hidden"
                style={backgroundStyles.mobile}
            ></div>
            
            {/* Content */}
            {showContent && (
                <div className="relative z-10 text-center px-6 max-w-4xl mx-auto">
                    {title && (
                        <h1 className="text-4xl md:text-5xl lg:text-6xl font-black text-white mb-4 tracking-tight">
                            <span className="bg-gradient-to-r from-yellow-400 via-orange-500 to-blue-500 bg-clip-text text-transparent">
                                {title}
                            </span>
                        </h1>
                    )}
                    {subtitle && (
                        <p className="text-lg md:text-xl text-gray-200 max-w-2xl mx-auto leading-relaxed">
                            {subtitle}
                        </p>
                    )}
                </div>
            )}
            
            {/* Wave decoration */}
            <div className="absolute bottom-0 left-0 right-0">
                <svg 
                    className="w-full h-16 fill-gray-900" 
                    viewBox="0 0 1200 120" 
                    preserveAspectRatio="none"
                >
                    <path d="M0,0V46.29c47.79,22.2,103.59,32.17,158,28,70.36-5.37,136.33-33.31,206.8-37.5C438.64,32.43,512.34,53.67,583,72.05c69.27,18,138.3,24.88,209.4,13.08,36.15-6,69.85-17.84,104.45-29.34C989.49,25,1113-14.29,1200,52.47V0Z" />
                </svg>
            </div>
        </div>
    );
};

export default PageHeader;